<template>
	<div class="dutyfree-website-products">
		<!-- Main Card Header & Action Tools -->
		<b-card header-tag="header" class="box-shadow-sm border-0 mb-4">
			<template #header>
				<div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
					<div class="d-flex align-items-center">
						<div class="icon-shape bg-primary text-white rounded-circle mr-3 d-flex align-items-center justify-content-center" style="width: 42px; height: 42px;">
							<i class="fa fa-globe font-lg"></i>
						</div>
						<div>
							<h4 class="mb-0 font-weight-bold">{{ $t('dutyfree_website.products_title') }}</h4>
							<small class="text-muted">{{ $t('dutyfree_website.products_subtitle') }}</small>
						</div>
					</div>
					<div class="d-flex align-items-center gap-2">
						<b-button variant="outline-primary" size="sm" @click="openSettingsModal" class="d-flex align-items-center">
							<i class="fa fa-cogs mr-1"></i> {{ $t('dutyfree_website.site_settings') }}
						</b-button>
						<b-button variant="outline-secondary" size="sm" @click="refreshData" class="d-flex align-items-center ml-2">
							<i class="fa fa-refresh mr-1" :class="{ 'fa-spin': loading }"></i> {{ $t('dutyfree_website.refresh') }}
						</b-button>
					</div>
				</div>
			</template>

			<!-- Quick Summary Stats Bar -->
			<b-row class="mb-4">
				<b-col md="4" sm="6" class="mb-2">
					<div class="p-3 bg-light rounded border d-flex align-items-center justify-content-between">
						<div>
							<span class="text-muted text-uppercase font-xs font-weight-bold">{{ $t('dutyfree_website.total_products') }}</span>
							<h3 class="mb-0 text-primary font-weight-bold">{{ total }}</h3>
						</div>
						<i class="fa fa-cubes fa-2x text-primary opacity-5"></i>
					</div>
				</b-col>
				<b-col md="4" sm="6" class="mb-2">
					<div class="p-3 bg-light rounded border d-flex align-items-center justify-content-between">
						<div>
							<span class="text-muted text-uppercase font-xs font-weight-bold">{{ $t('dutyfree_website.visible_on_site') }}</span>
							<h3 class="mb-0 text-success font-weight-bold">{{ visibleCount }}</h3>
						</div>
						<i class="fa fa-eye fa-2x text-success opacity-5"></i>
					</div>
				</b-col>
				<b-col md="4" sm="12" class="mb-2">
					<div class="p-3 bg-light rounded border d-flex align-items-center justify-content-between">
						<div>
							<span class="text-muted text-uppercase font-xs font-weight-bold">{{ $t('dutyfree_website.active_price_dept') }}</span>
							<div class="h5 mb-0 text-dark font-weight-bold truncate-text">
								{{ activeDepartmentName }}
							</div>
						</div>
						<i class="fa fa-building fa-2x text-info opacity-5"></i>
					</div>
				</b-col>
			</b-row>

			<!-- Filters Bar -->
			<b-card bg-variant="light" border-variant="light" class="mb-3" style="min-height: 50px !important">
				<b-row align-v="center">
					<!-- Keyword Search -->
					<b-col lg="3" md="6" sm="12" class="mb-2 mb-lg-0">
						<b-input-group size="sm">
							<template #prepend>
								<b-input-group-text><i class="fa fa-search"></i></b-input-group-text>
							</template>
							<b-form-input
								v-model="filters.keyword"
								:placeholder="$t('dutyfree_website.search_placeholder')"
								@keyup.enter="handleSearch"
							/>
							<template #append v-if="filters.keyword">
								<b-button size="sm" variant="outline-secondary" @click="filters.keyword = ''; handleSearch()">
									<i class="fa fa-times"></i>
								</b-button>
							</template>
						</b-input-group>
					</b-col>

					<!-- Group Select (Children of MAGAZIN) -->
					<b-col lg="2" md="6" sm="6" class="mb-2 mb-lg-0">
						<b-form-select
							v-model="filters.groupId"
							size="sm"
							@change="onGroupChange"
						>
							<option :value="null">-- {{ $t('dutyfree_website.all_groups') }} --</option>
							<option v-for="grp in groups" :key="grp.id" :value="grp.id">
								{{ getItemLabel(grp) }}
							</option>
						</b-form-select>
					</b-col>

					<!-- Category Select (Children of Selected Group) -->
					<b-col lg="2" md="6" sm="6" class="mb-2 mb-lg-0">
						<b-form-select
							v-model="filters.categoryId"
							size="sm"
							:disabled="!filters.groupId || loadingCategories"
							@change="handleSearch"
						>
							<option :value="null">-- {{ $t('dutyfree_website.all_categories') }} --</option>
							<option v-for="cat in categories" :key="cat.id" :value="cat.id">
								{{ getItemLabel(cat) }}
							</option>
						</b-form-select>
					</b-col>

					<!-- Department Select (Only Leaf Departments) -->
					<b-col lg="3" md="6" sm="6" class="mb-2 mb-lg-0">
						<b-form-select
							v-model="selectedDepartmentId"
							size="sm"
							@change="handleSearch"
						>
							<option :value="null">-- {{ $t('dutyfree_website.select_department_option') }} --</option>
							<option v-for="dept in departments" :key="dept.id" :value="dept.id">
								{{ getDepartmentLabel(dept) }}
							</option>
						</b-form-select>
					</b-col>

					<!-- Visibility Status Select -->
					<b-col lg="2" md="6" sm="6" class="mb-2 mb-lg-0">
						<b-form-select
							v-model="filters.visibilityStatus"
							size="sm"
							@change="handleSearch"
						>
							<option value="ALL">{{ $t('dutyfree_website.all_statuses') }}</option>
							<option value="VISIBLE">{{ $t('dutyfree_website.only_visible') }}</option>
							<option value="HIDDEN">{{ $t('dutyfree_website.only_hidden') }}</option>
						</b-form-select>
					</b-col>
				</b-row>
			</b-card>

			<!-- Bulk Operations Bar (If items selected) -->
			<div v-if="selectedItems.length > 0" class="alert alert-info d-flex align-items-center justify-content-between mb-3 py-2 px-3">
				<div>
					<i class="fa fa-check-square-o mr-2"></i>
					<strong>{{ selectedItems.length }}</strong> {{ $t('dutyfree_website.items_selected') }}
				</div>
				<div>
					<b-button variant="success" size="sm" class="mr-2" @click="bulkSetVisibility(true)">
						<i class="fa fa-eye mr-1"></i> {{ $t('dutyfree_website.show_selected_on_site') }}
					</b-button>
					<b-button variant="warning" size="sm" @click="bulkSetVisibility(false)">
						<i class="fa fa-eye-slash mr-1"></i> {{ $t('dutyfree_website.hide_selected_from_site') }}
					</b-button>
				</div>
			</div>

			<!-- Products Table -->
			<b-overlay :show="loading" rounded="sm" spinner-variant="primary">
				<div class="table-responsive">
					<table class="table table-bordered table-hover align-middle">
						<thead class="thead-light">
							<tr>
								<th class="text-center" style="width: 40px">
									<b-form-checkbox
										v-model="selectAll"
										@change="toggleSelectAll"
									/>
								</th>
								<th class="text-center" style="width: 50px">№</th>
								<th class="text-center" style="width: 80px">{{ $t('dutyfree_website.photo') }}</th>
								<th class="text-center">{{ $t('dutyfree_website.barcode') }}</th>
								<th>{{ $t('dutyfree_website.product_name') }}</th>
								<th class="text-center">{{ $t('dutyfree_website.category') }}</th>
								<th class="text-center">{{ $t('dutyfree_website.department_price') }}</th>
								<th class="text-center" style="width: 140px">{{ $t('dutyfree_website.site_visibility') }}</th>
								<th class="text-center" style="width: 100px">{{ $t('dutyfree_website.actions') }}</th>
							</tr>
						</thead>
						<tbody v-if="items.length > 0">
							<tr v-for="(item, index) in items" :key="item.id || index" :class="{ 'table-active': isItemSelected(item.id) }">
								<td class="text-center align-middle">
									<b-form-checkbox
										:value="item.id"
										v-model="selectedItems"
									/>
								</td>
								<td class="text-center align-middle font-weight-bold">
									{{ (payload.page - 1) * payload.limit + index + 1 }}
								</td>
								<td class="text-center align-middle">
									<img
										:src="getItemPhotoUrl(item)"
										:alt="item.name || item.nameUz"
										class="img-thumbnail rounded"
										style="width: 50px; height: 50px; object-fit: cover;"
										@error="handleImageError"
									/>
								</td>
								<td class="text-center align-middle font-monospace">
									<code>{{ item.barcode || '—' }}</code>
								</td>
								<td class="align-middle">
									<div class="font-weight-bold text-dark">{{ item.name || item.nameUz || item.nameRu }}</div>
								</td>
								<td class="align-middle text-center">
									<b-badge variant="light" class="border text-secondary">
										{{ getCategoryName(item) }}
									</b-badge>
								</td>
								<td class="text-center align-middle font-weight-bold text-primary">
									{{ formatPrice(item.price || item.departmentPrice || item.retailPrice) }}
								</td>
								<td class="text-center align-middle">
									<b-form-checkbox
										v-model="item.isWebsiteVisible"
										switch
										size="lg"
										@change="toggleVisibility(item)"
									>
										<span v-if="item.isWebsiteVisible" class="badge badge-success px-2 py-1">
											<i class="fa fa-eye"></i> {{ $t('dutyfree_website.visible') }}
										</span>
										<span v-else class="badge badge-secondary px-2 py-1">
											<i class="fa fa-eye-slash"></i> {{ $t('dutyfree_website.hidden') }}
										</span>
									</b-form-checkbox>
								</td>
								<td class="text-center align-middle">
									<b-button
										variant="outline-info"
										size="sm"
										class="btn-icon"
										v-b-tooltip.hover
										:title="$t('dutyfree_website.details')"
										@click="openDetailsModal(item)"
									>
										<i class="fa fa-info-circle"></i>
									</b-button>
								</td>
							</tr>
						</tbody>
						<tbody v-else>
							<tr>
								<td colspan="9" class="text-center py-5 text-muted">
									<i class="fa fa-folder-open-o fa-3x mb-2 opacity-5"></i>
									<p class="mb-0">{{ $t('dutyfree_website.no_products_found') }}</p>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- Pagination Bar -->
				<div class="d-flex justify-content-between align-items-center mt-3 flex-wrap">
					<div class="text-muted font-sm mb-2 mb-sm-0">
						{{ $t('dutyfree_website.showing') }}
						<b>{{ items.length > 0 ? (payload.page - 1) * payload.limit + 1 : 0 }}</b> -
						<b>{{ Math.min(payload.page * payload.limit, total) }}</b> /
						<b>{{ total }}</b>
					</div>
					<nav>
						<b-pagination-new
							background
							:total="total"
							layout="prev, pager, next"
							:page-size="payload.limit"
							:current-page.sync="payload.page"
							@current-change="loadItems"
						/>
					</nav>
				</div>
			</b-overlay>
		</b-card>

		<!-- Settings Modal: Department Price Selection -->
		<b-modal
			v-model="settingsModal"
			:title="$t('dutyfree_website.site_settings_title')"
			centered
			hide-footer
			header-bg-variant="primary"
			header-text-variant="white"
		>
			<b-form @submit.prevent="saveSettings">
				<b-form-group :label="$t('dutyfree_website.select_price_department')" label-for="dept-select">
					<b-form-select
						id="dept-select"
						v-model="selectedDepartmentId"
						:options="departmentOptions"
						required
					/>
					<small class="form-text text-muted mt-2">
						<i class="fa fa-info-circle mr-1"></i>
						{{ $t('dutyfree_website.dept_price_info_text') }}
					</small>
				</b-form-group>

				<hr />

				<div class="d-flex justify-content-end gap-2">
					<b-button variant="outline-secondary" size="sm" @click="settingsModal = false" class="mr-2">
						{{ $t('cancel') }}
					</b-button>
					<b-button variant="primary" size="sm" type="submit" :disabled="savingSettings">
						<b-spinner small v-if="savingSettings" class="mr-1"></b-spinner>
						{{ $t('save') }}
					</b-button>
				</div>
			</b-form>
		</b-modal>

		<!-- Details Modal -->
		<b-modal
			v-model="detailsModal"
			:title="currentItem ? (currentItem.name || currentItem.nameUz) : ''"
			centered
			hide-footer
		>
			<div v-if="currentItem" class="text-center mb-3">
				<img
					:src="getItemPhotoUrl(currentItem)"
					class="img-fluid rounded border mb-3"
					style="max-height: 180px; object-fit: contain;"
				/>
				<h5 class="font-weight-bold mb-1">{{ currentItem.name || currentItem.nameUz }}</h5>
				<b-badge variant="info">{{ getCategoryName(currentItem) }}</b-badge>
			</div>

			<table v-if="currentItem" class="table table-sm table-bordered">
				<tr>
					<th>{{ $t('dutyfree_website.barcode') }}</th>
					<td><code>{{ currentItem.barcode || currentItem.barCode || '—' }}</code></td>
				</tr>
				<tr>
					<th>{{ $t('dutyfree_website.code') }}</th>
					<td>{{ currentItem.code || currentItem.artikul || '—' }}</td>
				</tr>
				<tr>
					<th>{{ $t('dutyfree_website.active_price_dept') }}</th>
					<td>{{ activeDepartmentName }}</td>
				</tr>
				<tr>
					<th>{{ $t('dutyfree_website.department_price') }}</th>
					<td class="font-weight-bold text-success">{{ formatPrice(currentItem.price || currentItem.departmentPrice) }}</td>
				</tr>
				<tr>
					<th>{{ $t('dutyfree_website.site_visibility') }}</th>
					<td>
						<span v-if="currentItem.isWebsiteVisible" class="text-success font-weight-bold">
							<i class="fa fa-check-circle"></i> {{ $t('dutyfree_website.visible') }}
						</span>
						<span v-else class="text-secondary">
							<i class="fa fa-times-circle"></i> {{ $t('dutyfree_website.hidden') }}
						</span>
					</td>
				</tr>
			</table>

			<div class="text-right mt-3">
				<b-button variant="secondary" size="sm" @click="detailsModal = false">
					{{ $t('close') }}
				</b-button>
			</div>
		</b-modal>
	</div>
</template>

<script>
import DutyFreeWebsiteService from '@/services/dutyfree-website.service'
import { BOverlay } from 'bootstrapVue2'
import { Pagination } from 'element-ui'

export default {
	name: 'DutyFreeWebsiteProducts',
	components: {
		BOverlay,
		'b-pagination-new': Pagination
	},
	data() {
		return {
			loading: false,
			loadingCategories: false,
			savingSettings: false,
			settingsModal: false,
			detailsModal: false,
			items: [],
			groups: [],
			categories: [],
			departments: [],
			selectedDepartmentId: null,
			total: 0,
			visibleCount: 0,
			selectedItems: [],
			selectAll: false,
			currentItem: null,
			filters: {
				keyword: '',
				groupId: null,
				categoryId: null,
				visibilityStatus: 'ALL'
			},
			payload: {
				page: 1,
				limit: 20
			}
		}
	},
	computed: {
		activeDepartmentName() {
			if (!this.selectedDepartmentId || !this.departments.length) {
				return this.$t('dutyfree_website.not_selected')
			}
			const found = this.departments.find(d => d.id === this.selectedDepartmentId)
			return found ? this.getDepartmentLabel(found) : this.$t('dutyfree_website.default_dept')
		},
		departmentOptions() {
			const options = [
				{ value: null, text: this.$t('dutyfree_website.select_department_option') }
			]
			this.departments.forEach(d => {
				options.push({
					value: d.id,
					text: this.getDepartmentLabel(d)
				})
			})
			return options
		}
	},
	async created() {
		await this.initData()
	},
	methods: {
		async initData() {
			await Promise.all([
				this.loadGroups(),
				this.loadDepartments(),
				this.loadWebsiteSettings()
			])
			await this.loadItems()
		},

		/**
		 * Extract leaf departments (nodes with no sub-departments/children)
		 */
		getLeafDepartments(list) {
			const leaves = []
			if (!Array.isArray(list)) return leaves

			const traverse = (node) => {
				const children = node.children || node.child || node.subDepartments || []
				if (Array.isArray(children) && children.length > 0) {
					children.forEach(child => traverse(child))
				} else {
					leaves.push(node)
				}
			}

			list.forEach(node => traverse(node))
			return leaves
		},

		async loadGroups() {
			try {
				const groups = await DutyFreeWebsiteService.fetchMagazinGroups()
				this.groups = Array.isArray(groups) ? groups : []
			} catch (e) {
				console.warn('Could not load Magazin Groups:', e)
			}
		},

		async onGroupChange(groupId) {
			this.filters.categoryId = null
			this.categories = []

			if (groupId) {
				this.loadingCategories = true
				try {
					const catList = await DutyFreeWebsiteService.fetchCategoriesByGroup(groupId)
					this.categories = Array.isArray(catList) ? catList : []
				} catch (e) {
					console.warn('Could not load categories for group:', groupId, e)
				} finally {
					this.loadingCategories = false
				}
			}
			this.handleSearch()
		},

		async loadDepartments() {
			try {
				const res = await DutyFreeWebsiteService.fetchDepartments()
				const data = res.data || res || []
				const rawList = Array.isArray(data) ? data : (data.items || data.data || data.content || [])
				this.departments = this.getLeafDepartments(rawList)
			} catch (e) {
				console.warn('Could not load departments:', e)
			}
		},

		async loadWebsiteSettings() {
			try {
				const res = await DutyFreeWebsiteService.getWebsiteSettings()
				if (res && res.data && res.data.departmentId) {
					this.selectedDepartmentId = res.data.departmentId
				}
			} catch (e) {
				console.warn('Using default department settings')
			}
		},

		async loadItems() {
			this.loading = true
			try {
				const queryPayload = {
					page: this.payload.page,
					limit: this.payload.limit,
					keyword: this.filters.keyword,
					groupId: this.filters.groupId,
					categoryId: this.filters.categoryId,
					visibilityStatus: this.filters.visibilityStatus,
					departmentId: this.selectedDepartmentId
				}

				const res = await DutyFreeWebsiteService.fetchProducts(queryPayload)
				if (res && res.data) {
					const itemList = res.data.items || res.data.content || (Array.isArray(res.data) ? res.data : [])
					this.items = itemList.map(item => ({
						...item,
						isWebsiteVisible: item.isWebsiteVisible !== undefined ? Boolean(item.isWebsiteVisible) : true
					}))
					this.total = res.data.total || res.data.totalElements || itemList.length
					this.visibleCount = this.items.filter(i => i.isWebsiteVisible).length
				}
			} catch (e) {
				if (this.$vs && this.$vs.notify) {
					this.$vs.notify({
						title: this.$t('error'),
						text: e.message || 'Xatolik yuz berdi',
						color: 'danger',
						position: 'top-right'
					})
				}
			} finally {
				this.loading = false
			}
		},

		handleSearch() {
			this.payload.page = 1
			this.loadItems()
		},

		async refreshData() {
			await this.loadItems()
		},

		async toggleVisibility(item) {
			try {
				await DutyFreeWebsiteService.updateVisibility(item.id, item.isWebsiteVisible)
				this.visibleCount = this.items.filter(i => i.isWebsiteVisible).length
				if (this.$vs && this.$vs.notify) {
					this.$vs.notify({
						title: this.$t('success'),
						text: item.isWebsiteVisible
							? this.$t('dutyfree_website.item_shown_msg')
							: this.$t('dutyfree_website.item_hidden_msg'),
						color: 'success',
						position: 'top-right'
					})
				}
			} catch (e) {
				item.isWebsiteVisible = !item.isWebsiteVisible
				if (this.$vs && this.$vs.notify) {
					this.$vs.notify({
						title: this.$t('error'),
						text: e.message || 'Holatni saqlashda xatolik',
						color: 'danger',
						position: 'top-right'
					})
				}
			}
		},

		async bulkSetVisibility(isVisible) {
			if (!this.selectedItems.length) return
			this.loading = true
			try {
				await DutyFreeWebsiteService.updateBulkVisibility(this.selectedItems, isVisible)
				this.items.forEach(item => {
					if (this.selectedItems.includes(item.id)) {
						item.isWebsiteVisible = isVisible
					}
				})
				this.selectedItems = []
				this.selectAll = false
				this.visibleCount = this.items.filter(i => i.isWebsiteVisible).length
				if (this.$vs && this.$vs.notify) {
					this.$vs.notify({
						title: this.$t('success'),
						text: this.$t('dutyfree_website.bulk_update_success'),
						color: 'success',
						position: 'top-right'
					})
				}
			} catch (e) {
				if (this.$vs && this.$vs.notify) {
					this.$vs.notify({
						title: this.$t('error'),
						text: e.message || 'Ommaviy o\'zgartirishda xatolik',
						color: 'danger',
						position: 'top-right'
					})
				}
			} finally {
				this.loading = false
			}
		},

		openSettingsModal() {
			this.settingsModal = true
		},

		async saveSettings() {
			this.savingSettings = true
			try {
				await DutyFreeWebsiteService.saveWebsiteDepartmentSetting(this.selectedDepartmentId)
				this.settingsModal = false
				if (this.$vs && this.$vs.notify) {
					this.$vs.notify({
						title: this.$t('success'),
						text: this.$t('dutyfree_website.settings_saved'),
						color: 'success',
						position: 'top-right'
					})
				}
				await this.loadItems()
			} catch (e) {
				if (this.$vs && this.$vs.notify) {
					this.$vs.notify({
						title: this.$t('error'),
						text: e.message || 'Sozlamalarni saqlashda xatolik',
						color: 'danger',
						position: 'top-right'
					})
				}
			} finally {
				this.savingSettings = false
			}
		},

		openDetailsModal(item) {
			this.currentItem = item
			this.detailsModal = true
		},

		toggleSelectAll(checked) {
			if (checked) {
				this.selectedItems = this.items.map(i => i.id)
			} else {
				this.selectedItems = []
			}
		},

		isItemSelected(id) {
			return this.selectedItems.includes(id)
		},

		getItemLabel(el) {
			if (!el) return ''
			return el.name || el.nameUz || el.nameRu || el.code || `Item #${el.id}`
		},

		getDepartmentLabel(dept) {
			if (!dept) return ''
			return dept.name || dept.nameRu || dept.nameUz || dept.shortName || `Department #${dept.id}`
		},

		getCategoryName(item) {
			return `${item.subCategoryName} / ${item.sub2CategoryName}`
		},

		getItemPhotoUrl(item) {
			if (item.photoId || item.mainPhotoId) {
				return process.env.VUE_APP_ROOT_API + '/item/photo/' + (item.photoId || item.mainPhotoId)
			}
			if (item.image) {
				return item.image
			}
			return require('../../assets/item.png')
		},

		handleImageError(e) {
			e.target.src = require('../../assets/item.png')
		},

		formatPrice(val) {
			if (val === undefined || val === null || isNaN(val)) return '0 €'
			return Number(val).toLocaleString('ru-RU') + ' €'
		}
	}
}
</script>

<style scoped lang="scss">
.dutyfree-website-products {
	.font-monospace {
		font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
	}
	.truncate-text {
		max-width: 180px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.btn-icon {
		padding: 0.25rem 0.5rem;
	}
	.opacity-5 {
		opacity: 0.25;
	}
}
</style>
